import { Controller } from "@hotwired/stimulus"

// Formulaire admin — les éditions d'un produit (prix + fichiers)
//   • ajoute / retire une édition
//   • garde price_cents (caché, millimes) en phase avec le prix public saisi
//   • cache le bloc « identité » (nom, tagline, corbeille) tant qu'il n'y a
//     qu'une édition : le produit se vend sous son titre, sans rien nommer
//     (décision d'Omar du 06/09/2026, 4 produits sur 4 en prod à une seule)
//   • liste les fichiers choisis (plusieurs par édition) et ceux à retirer
//   • redessine l'aperçu du checkout à chaque saisie
//
// Cibles :
//   list, template, row, destroy, position   : la mécanique des lignes
//   priceTnd, priceCents                     : prix public visible / millimes cachés
//   identite, name, description, emoji       : le bloc nommé, visible à partir de deux
//   titre                                    : le champ titre du produit (nom de l'édition unique)
//   requiresShipping, shippingConfig         : héritage des éditions physiques (avant le 25/08/2026)
//   digitalFile, fileName, fichiersJoints, retirer : les fichiers
//   preview, previewHeader, intro            : l'aperçu et les textes qui changent avec le nombre
//
// Le clonage passe par DOMParser, jamais par innerHTML sur un nœud vivant.
export default class extends Controller {
  static targets = [
    "list", "template", "row", "destroy", "position",
    "priceTnd", "priceCents", "identite", "name", "description", "emoji", "titre",
    "requiresShipping", "shippingConfig", "digitalFile", "fileName", "fichiersJoints", "retirer",
    "preview", "previewHeader", "intro"
  ]

  connect() {
    this.priceTndTargets.forEach((el) => this.attachPriceSync(el))
    this.reindexPositions()
    this.render()
  }

  add(event) {
    event.preventDefault()
    const uid = Date.now().toString()
    const raw = this.templateTarget.innerHTML.replaceAll("NEW_RECORD", uid)
    const doc = new DOMParser().parseFromString(raw, "text/html")
    const newRow = doc.body.firstElementChild
    if (!newRow) return
    this.listTarget.appendChild(newRow)
    newRow.querySelectorAll('[data-book-variants-target="priceTnd"]')
          .forEach((el) => this.attachPriceSync(el))
    this.reindexPositions()
    this.render()
    // La deuxième édition demande un nom : on y amène le curseur.
    const nom = newRow.querySelector('[data-book-variants-target="name"]')
    if (nom && this.visibleRows().length > 1) nom.focus({ preventScroll: true })
    // Le bouton est dans l'en-tête de la section : la carte naît plus bas, hors écran.
    const reduit = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    newRow.scrollIntoView({ block: "start", behavior: reduit ? "auto" : "smooth" })
  }

  remove(event) {
    event.preventDefault()
    const row = event.target.closest('[data-book-variants-target="row"]')
    if (!row) return
    const destroyInput = row.querySelector('[data-book-variants-target="destroy"]')
    if (destroyInput) {
      destroyInput.value = "1"
      row.style.display = "none"
    } else {
      row.remove()
    }
    this.reindexPositions()
    this.render()
  }

  // Relié à input/change sur chaque nom, prix, fichier, titre.
  render() {
    this.refreshMode()
    this.refreshFileNames()
    this.refreshRowEmojis()
    this.refreshShippingConfigVisibility()
    this.renderPreview()
  }

  visibleRows() {
    return this.rowTargets.filter((row) => row.style.display !== "none")
  }

  // Une édition : rien à nommer, le bloc identité reste caché. Deux ou plus :
  // le bloc apparaît partout, et la première prend le titre si elle n'a pas de nom.
  refreshMode() {
    const rows = this.visibleRows()
    const plusieurs = rows.length > 1
    rows.forEach((row, i) => {
      const bloc = row.querySelector('[data-book-variants-target="identite"]')
      if (bloc) bloc.style.display = plusieurs ? "" : "none"
      if (plusieurs && i === 0) {
        const nom = row.querySelector('[data-book-variants-target="name"]')
        if (nom && nom.value.trim() === "") nom.value = this.titre()
      }
    })
    if (this.hasIntroTarget) {
      this.introTarget.textContent = plusieurs
        ? "Le client choisit une édition, paie son prix et reçoit ses fichiers juste après le paiement."
        : "Le client paie ce prix et reçoit ces fichiers juste après le paiement."
    }
  }

  titre() {
    return this.hasTitreTarget ? this.titreTarget.value.trim() : ""
  }

  // Depuis le 23/10/2026 les fichiers partent dès qu'ils sont choisis
  // (fichier-livre) : c'est lui qui affiche nom, taille et avancement. Ici,
  // on ne compte plus que ce qui est prêt. L'événement fichier-livre:change
  // remonte jusqu'au formulaire, qui rappelle render().
  refreshFileNames() {}

  refreshRowEmojis() {
    this.rowTargets.forEach((row) => {
      const emojiEl = row.querySelector('[data-book-variants-target="emoji"]')
      if (!emojiEl) return
      emojiEl.textContent = this.emojiFor(row)
    })
  }

  // Affiche / cache la section « Sous-champs de l'adresse » selon la case
  // « Adresse de livraison demandée » de chaque édition héritée.
  refreshShippingConfigVisibility() {
    this.rowTargets.forEach((row) => {
      const block = row.querySelector('[data-book-variants-target="shippingConfig"]')
      if (!block) return
      block.style.display = this.rowRequiresShipping(row) ? "" : "none"
    })
  }

  emojiFor(row) {
    const physical = this.rowRequiresShipping(row)
    const digital  = this.rowFileCount(row) > 0
    // Beb ne vend plus que du numérique (25/08/2026) : 📦 et 🎁 ne
    // survivent que sur une édition héritée qui demande encore une adresse.
    if (physical && digital) return "🎁"
    if (physical)            return "📦"
    return "📄"
  }

  rowRequiresShipping(row) {
    const cb = row.querySelector('[data-book-variants-target="requiresShipping"]')
    return cb ? cb.checked : false
  }

  // Fichiers que le client recevra si on enregistre maintenant :
  // ceux envoyés et prêts + ceux déjà joints et non cochés « retirer ».
  rowFileCount(row) {
    const choisis = row.querySelectorAll('.bv-envoi[data-etat="pret"]').length
    const joints = row.querySelectorAll('[data-book-variants-target="fichiersJoints"] > li').length
    const retires = row.querySelectorAll('[data-book-variants-target="retirer"]:checked').length
    return choisis + joints - retires
  }

  attachPriceSync(tndInput) {
    if (tndInput.dataset.bookVariantsBound) return
    tndInput.dataset.bookVariantsBound = "1"
    const row = tndInput.closest('[data-book-variants-target="row"]')
    const cents = row && row.querySelector('[data-book-variants-target="priceCents"]')
    if (!cents) return
    const sync = () => {
      const tnd = parseFloat(tndInput.value || "0")
      cents.value = Math.round(tnd * 1000)
    }
    tndInput.addEventListener("input", sync)
    sync()
  }

  reindexPositions() {
    let pos = 1
    this.rowTargets.forEach((row) => {
      if (row.style.display === "none") return
      const posInput = row.querySelector('[data-book-variants-target="position"]')
      if (posInput) posInput.value = pos++
    })
  }

  // ─── Aperçu du checkout ──────────────────────────────────────────────────

  renderPreview() {
    if (!this.hasPreviewTarget) return
    const variants = this.collectVisibleVariants()
    const plusieurs = variants.length > 1
    if (this.hasPreviewHeaderTarget) this.previewHeaderTarget.style.display = plusieurs ? "" : "none"

    if (variants.length === 0) {
      const txt = this.previewTarget.dataset.emptyText ||
                  "Indiquez un prix pour voir l'aperçu."
      this.previewTarget.replaceChildren(this.empty(txt))
      return
    }

    const frag = document.createDocumentFragment()
    variants.forEach((v, i) => frag.appendChild(this.previewCard(v, i === 0, plusieurs)))
    this.previewTarget.replaceChildren(frag)
  }

  // Une édition seule se montre sous le titre du produit dès qu'un prix est
  // saisi ; à partir de deux, chacune doit avoir un nom pour apparaître.
  collectVisibleVariants() {
    const rows = this.visibleRows()
    const seule = rows.length === 1
    return rows
      .map((row) => ({
        name: (row.querySelector('[data-book-variants-target="name"]')?.value || "").trim(),
        priceTnd: parseFloat(row.querySelector('[data-book-variants-target="priceTnd"]')?.value || "0") || 0,
        physical: this.rowRequiresShipping(row),
        files: this.rowFileCount(row)
      }))
      .map((v) => (seule && v.name.length === 0 ? { ...v, name: this.titre() || "Votre produit" } : v))
      .filter((v) => v.name.length > 0 && (!seule || v.priceTnd > 0 || v.files > 0))
  }

  // Une carte d'aperçu, construite par l'API DOM : jamais d'innerHTML sur un nœud vivant.
  previewCard(v, selected, avecRadio) {
    const label = document.createElement("label")
    label.className = "flex items-center gap-3"
    label.style.padding = "12px"
    label.style.borderRadius = "10px"
    label.style.marginBottom = "8px"
    label.style.cursor = avecRadio ? "pointer" : "default"
    label.style.border = selected ? "2px solid var(--brand)" : "1px solid var(--line)"
    label.style.background = selected ? "var(--brand-soft)" : "var(--surface)"

    if (avecRadio) {
      const radio = document.createElement("input")
      radio.type = "radio"
      radio.name = "preview_variant"
      if (selected) radio.checked = true
      radio.disabled = true
      label.appendChild(radio)
    }

    const main = document.createElement("div")
    main.style.flex = "1"
    main.style.minWidth = "0"

    const row = document.createElement("div")
    row.className = "flex items-center gap-2 flex-wrap"
    const nameEl = document.createElement("span")
    nameEl.style.fontSize = "14px"
    nameEl.style.fontWeight = "500"
    nameEl.textContent = v.name
    row.appendChild(nameEl)

    if (v.physical) {
      const chip = document.createElement("span")
      chip.className = "chip"
      chip.style.fontSize = "10px"
      chip.textContent = "Livraison"
      row.appendChild(chip)
    }
    const chip = document.createElement("span")
    chip.className = "chip"
    chip.style.fontSize = "10px"
    if (v.files > 0) {
      chip.textContent = v.files === 1 ? "1 fichier" : `${v.files} fichiers`
    } else {
      chip.textContent = "Aucun fichier"
      chip.style.color = "var(--danger-text)" // --danger rendait 4,43:1 sur la pastille (17/10/2026)
    }
    row.appendChild(chip)
    main.appendChild(row)
    label.appendChild(main)

    const price = document.createElement("span")
    price.style.fontWeight = "600"
    price.textContent = this.formatPrice(v.priceTnd)
    label.appendChild(price)

    return label
  }

  empty(message) {
    const el = document.createElement("p")
    el.className = "muted"
    el.style.fontSize = "13px"
    el.style.padding = "16px 4px"
    el.textContent = message
    return el
  }

  formatPrice(tnd) {
    if (!tnd || tnd === 0) return "Gratuit"
    return Number.isInteger(tnd) ? `${tnd} TND` : `${tnd.toFixed(2)} TND`
  }
}
